import { isSessionExpired } from './utils/session.js';
import { Account } from './multiAccounts/data.ts';
import { ParsedHomeQueryParams } from './parsedQueryParams.ts';

export type StepReg = '1' | '2';

type EventOpts = {
  webappRegType: string;
  sessionExpires: number;
};

function isOpenLinkByEmail(parsedQueryParams: ParsedHomeQueryParams) {
  return parsedQueryParams.fr === 'false';
}

function isRegByIdFirst(eventOpts: EventOpts) {
  return eventOpts.webappRegType === 'by-id-first';
}

function isFirstStepOfReg(stepReg: StepReg) {
  return stepReg === '1';
}

function isRegistered(account: Account | void): account is Account {
  return Boolean(account);
}

function isNeedToAddPhoto(parsedQueryParams: ParsedHomeQueryParams) {
  return Boolean(parsedQueryParams.imgSrc) && Boolean(parsedQueryParams.photoId);
}

function isNeedToUnlock(parsedQueryParams: ParsedHomeQueryParams) {
  return Boolean(parsedQueryParams.token);
}

function isNeedToShowQR(parsedQueryParams: ParsedHomeQueryParams) {
  return Boolean(parsedQueryParams.showQR);
}

function isNeedToAddUGC(parsedQueryParams: ParsedHomeQueryParams) {
  return Boolean(parsedQueryParams.uploadUGC);
}

function isNeedToEnableTurnMeIntoTag(parsedQueryParams: ParsedHomeQueryParams) {
  return Boolean(parsedQueryParams.tg);
}

function isNeedToShowAiKiosk(parsedQueryParams: ParsedHomeQueryParams) {
  return Boolean(parsedQueryParams.aiKiosk);
}

export type GetModeParams = {
  parsedQueryParams: ParsedHomeQueryParams;
  eventOpts: EventOpts;
  stepReg: StepReg;
  account: Account | void;
};

function getRegFirstMode(
  { parsedQueryParams, eventOpts, stepReg, account }: GetModeParams,
  { currentDate = new Date() } = {}
) {
  if (parsedQueryParams.eventCode && !parsedQueryParams.attendeeUid) {
    if (isRegistered(account) && isSessionExpired(account.regDate, eventOpts, { currentDate })) {
      return 'registrationByIdFirst:sessionExpired';
    }
  }

  if (isFirstStepOfReg(stepReg)) {
    return 'registrationByIdFirst:notFinishedRegistration';
  }

  if (!isRegistered(account)) {
    return 'registrationByIdFirst:notRegistered';
  }
}

function getRegDefaultMode(
  {
    parsedQueryParams,
    eventOpts,
    account,
  }: {
    parsedQueryParams: ParsedHomeQueryParams;
    eventOpts: EventOpts;
    account: Account | void;
  },
  { currentDate = new Date() } = {}
) {
  if (!isRegistered(account)) {
    return 'registrationByDefault:notRegistered';
  }
  if (
    isRegistered(account) &&
    parsedQueryParams.eventCode &&
    !parsedQueryParams.attendeeUid &&
    isSessionExpired(account.regDate, eventOpts, { currentDate })
  ) {
    return 'registrationByDefault:sessionExpired';
  }
}

export function getMode(
  { parsedQueryParams, eventOpts, stepReg, account }: GetModeParams,
  { currentDate = new Date() } = {}
) {
  if (isOpenLinkByEmail(parsedQueryParams)) return 'openLinkByEmail';

  if (isRegByIdFirst(eventOpts)) {
    const mode = getRegFirstMode({ parsedQueryParams, eventOpts, stepReg, account }, { currentDate });
    if (mode) return mode;
  } else {
    const mode = getRegDefaultMode({ parsedQueryParams, eventOpts, account }, { currentDate });
    if (mode) return mode;
  }

  if (isNeedToShowQR(parsedQueryParams)) return 'showQR';

  if (isNeedToAddUGC(parsedQueryParams)) return 'addUGC';

  if (isNeedToEnableTurnMeIntoTag(parsedQueryParams)) return 'turnMeIntoTag';

  if (isNeedToAddPhoto(parsedQueryParams)) return 'addPhoto';

  if (isNeedToUnlock(parsedQueryParams)) return 'unlock';

  if (isNeedToShowAiKiosk(parsedQueryParams)) return 'aiKiosk';

  return 'default';
}
